// =============================================================================
// components/ui/MediaTransportBar.tsx — floating bottom transport bar for media
// (DARK glass). A play/pause control, an elapsed→remaining time pair, a thin
// scrub track, and an optional trailing control cluster (mute, fullscreen, …).
//
// Reference: the IG live-player bottom chrome — a translucent transport bar
// floating over full-bleed media (content runs underneath, apple-design SKILL
// §12). Absolute at the bottom with gutter side margins, clearing the home
// indicator via the safe-area inset.
//
// Purely presentational: the scrub track reflects `progress` (0–1); dragging is
// left to the host (no new gesture dep). Built from GlassSurface (mode='dark')
// + Icon.
// =============================================================================
import React from 'react';
import { View, Text, Pressable, StyleSheet, StyleProp, ViewStyle } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons, IoniconName } from './Icon';
import { GlassSurface } from '../glass';
import { glassTokens, glassColorTokens } from '../glass/tokens';
import { colors, spacing, fonts, layout } from '../../constants/theme';

export interface MediaTransportAction {
  key: string;
  icon: IoniconName;
  onPress: () => void;
  active?: boolean;
  accessibilityLabel?: string;
}

export interface MediaTransportBarProps {
  /** Playback progress 0–1. Drives the scrub track fill. */
  progress?: number;
  playing?: boolean;
  onPlayPause?: () => void;
  /** Formatted time strings, e.g. "1:24" / "-2:10". */
  elapsedLabel?: string;
  remainingLabel?: string;
  /** Trailing controls (mute, fullscreen, …). */
  actions?: MediaTransportAction[];
  /** Accent used for the filled portion of the track. */
  accent?: string;
  style?: StyleProp<ViewStyle>;
}

export default function MediaTransportBar({
  progress = 0,
  playing = false,
  onPlayPause,
  elapsedLabel,
  remainingLabel,
  actions,
  accent = colors.primaryLight,
  style,
}: MediaTransportBarProps) {
  const insets = useSafeAreaInsets();
  const pct = Math.max(0, Math.min(1, progress));

  return (
    <View
      pointerEvents="box-none"
      style={[styles.wrap, { bottom: Math.max(insets.bottom, spacing.md) + spacing.sm }, style]}
    >
      <GlassSurface
        mode="light"
        variant="bar"
        radius={glassTokens.radius.xl}
        contentStyle={styles.bar}
      >
        <View style={styles.controlsRow}>
          {onPlayPause ? (
            <Pressable
              onPress={onPlayPause}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              style={styles.playBtn}
              accessibilityRole="button"
              accessibilityLabel={playing ? 'Pause' : 'Play'}
            >
              <Ionicons name={playing ? 'pause' : 'play'} size={20} color={colors.white} />
            </Pressable>
          ) : null}

          {elapsedLabel ? <Text style={styles.time}>{elapsedLabel}</Text> : null}

          <View style={styles.track}>
            <View style={[styles.trackFill, { width: `${pct * 100}%`, backgroundColor: accent }]} />
            <View style={[styles.knob, { left: `${pct * 100}%`, backgroundColor: accent }]} />
          </View>

          {remainingLabel ? <Text style={styles.time}>{remainingLabel}</Text> : null}

          {actions?.length ? (
            <View style={styles.actions}>
              {actions.map((a) => (
                <Pressable
                  key={a.key}
                  onPress={a.onPress}
                  hitSlop={{ top: 8, bottom: 8, left: 6, right: 6 }}
                  style={styles.actionBtn}
                  accessibilityRole="button"
                  accessibilityLabel={a.accessibilityLabel ?? a.key}
                >
                  <Ionicons
                    name={a.icon}
                    size={18}
                    color={a.active ? accent : 'rgba(255,255,255,0.85)'}
                  />
                </Pressable>
              ))}
            </View>
          ) : null}
        </View>
      </GlassSurface>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    position: 'absolute',
    left: layout.gutter,
    right: layout.gutter,
  },
  bar: { paddingHorizontal: spacing.md, paddingVertical: spacing.sm + 2 },
  controlsRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  playBtn: { width: 32, height: 32, alignItems: 'center', justifyContent: 'center' },
  time: { ...fonts.semibold, fontSize: 12, color: 'rgba(255,255,255,0.8)', minWidth: 34, textAlign: 'center' },
  track: {
    flex: 1,
    height: 4,
    borderRadius: 2,
    backgroundColor: glassColorTokens.dark.border,
    justifyContent: 'center',
  },
  trackFill: { position: 'absolute', left: 0, top: 0, bottom: 0, borderRadius: 2 },
  knob: {
    position: 'absolute',
    width: 12, height: 12, borderRadius: 6,
    marginLeft: -6,
    top: -4,
    shadowColor: '#000', shadowOpacity: 0.3, shadowRadius: 3, shadowOffset: { width: 0, height: 1 },
  },
  actions: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs },
  actionBtn: { width: 30, height: 30, alignItems: 'center', justifyContent: 'center' },
});
